/* eslint-disable no-shadow */
import React, { useMemo } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Form as FinalForm } from 'react-final-form';
import { FormattedMessage, injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import { shape, func, arrayOf, bool, string } from 'prop-types';
import moment from 'moment';
import format from 'date-fns/format';
import Decimal from 'decimal.js';
import { get } from 'lodash';
import { types as sdkTypes } from '../../util/sdkLoader';
import config from '../../config';
import { propTypes } from '../../util/types';
import { createSlug } from '../../util/urlHelpers';
import { daysAdded, getDaysFromTimeslots } from '../../util/dates';
import { readStoredInquirySelection } from '../../util/inquirySelection';
import { getLodgingTax } from '../../util/listing';
import {
  formatPricingFormValues,
  getRVDetails,
  hasLodgingForPackage,
  isFlexiblePricingEnabled,
  getFlexiblePricingData,
} from '../../util/package';
import { isLegacyPaymentFlow } from '../../util/transaction';
import PackageStartingPrice from './PackageStartingPrice';
import { initializeCardPaymentData } from '../../ducks/stripe.duck';
import { createResourceLocatorString, findRouteByRouteName } from '../../util/routes';
import { EarlyAccessButtonWithTimer, PackageBadges, Banner } from '..';
import routeConfiguration from '../../routeConfiguration';
import PackagePricingForm from './components/PackagePricingForm';

import css from './PackagePricingPanel.module.css';

const { Money } = sdkTypes;

const PackagePricingPanel = ({ intl, ...rest }) => {
  const {
    history,
    packageInfo,
    includedLodging,
    optionalLodging,
    timeSlots,
    listing,
    unitType,
    startingPrice,
    currentUser,
    callSetInitialValues,
    onInitializeCardPaymentData,
    sticky,
    rootClassName,
    transaction,
    isAvailable,
    outOfBookingRange,
    isBlockedByEarlyAccess,
    formattedPackageAvailableDate,
    availabilityPackageBadges,
    setIsPackageAvailabilityModalOpen,
    isOnWaitingList,
    setIsEarlyAccessModalOpen,
    setIsCredovaModalOpen,
    currentUserInit,
    onPriceChange,
    showCredova,
    onMessage,
    onBookingSelectionChange,
    landownerFirstName,
    isOwnListing,
  } = rest;
  const availableDays = getDaysFromTimeslots(timeSlots);
  const { price: unitPrice, state: listingState } = listing.attributes;
  const { days, isPublished } = packageInfo || {};

  // guest/party size info
  const packageMinParty = get(packageInfo, 'guests.min', 1);
  const packageMaxParty = get(packageInfo, 'guests.max', 1);

  const currentTransaction = transaction || get(history, 'location.state.currentTransaction', null);
  const hasLodging = hasLodgingForPackage(packageInfo, false);

  const handleFormSubmit = values => {
    const { initialValues, saveToSessionStorage } = formatPricingFormValues(
      values,
      packageMinParty,
      listing,
      hasLodging,
      currentUser
    );

    const routes = routeConfiguration();

    // Customize checkout page state with current listing and selected bookingDates
    const { setInitialValues } = findRouteByRouteName('CheckoutPage', routes);
    callSetInitialValues(setInitialValues, initialValues, saveToSessionStorage);
    // Clear previous Stripe errors from store if there is any
    onInitializeCardPaymentData();

    const nextRoute = createResourceLocatorString(
      'CheckoutPackagePage',
      routes,
      {
        id: listing.id.uuid,
        packageId: packageInfo.id,
        slug: createSlug(listing.attributes.title),
      },
      {}
    );

    if (isLegacyPaymentFlow(currentTransaction)) {
      history.push(nextRoute, { currentTransaction: null });
    } else {
      history.push(nextRoute, { currentTransaction });
    }
  };

  const flexiblePricingData = getFlexiblePricingData(
    packageInfo?.flexiblePricing,
    packageInfo?.guests?.min
  );

  const storedInquiry = useMemo(() => readStoredInquirySelection(transaction), [transaction]);
  const optionalLodgingKey = Array.isArray(optionalLodging)
    ? optionalLodging.map(item => item?.type || item?.index || '').join(',')
    : '';

  const initialValues = useMemo(() => {
    const seeded = {};

    if (storedInquiry?.dates) {
      seeded.dates = storedInquiry.dates;
    }

    if (storedInquiry?.lodgingAddons?.length && optionalLodging) {
      const knownAddons = storedInquiry.lodgingAddons.filter(index => optionalLodging[index]);
      if (knownAddons.length) {
        seeded.lodgingAddons = knownAddons;
      }
    }

    if (storedInquiry?.partySize != null) {
      seeded.partySize = storedInquiry.partySize;
      return seeded;
    }

    if (isFlexiblePricingEnabled(packageInfo.activity)) {
      const initialPartySize =
        packageInfo?.guests?.max === 0
          ? 2 // adult non hunter + youth hunter
          : flexiblePricingData.adultHunter.quantity;

      const flexibleGuestSize =
        packageInfo?.guests?.max === 0
          ? {
              youthHunter: 1,
              adultNonHunter: 1,
            }
          : {
              adultHunter: packageMinParty || 1,
            };

      return {
        ...seeded,
        partySize: {
          partySize: initialPartySize,
          flexibleGuestSize,
        },
      };
    }

    return seeded;
    // optionalLodging is keyed by optionalLodgingKey so a new array with the
    // same lodging types does not rebuild initial values and reset the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    storedInquiry,
    optionalLodgingKey,
    packageInfo.activity,
    flexiblePricingData.adultHunter.quantity,
    packageInfo?.guests?.max,
    packageMinParty,
  ]);

  if (!currentUserInit) {
    return null;
  }

  return (
    <FinalForm
      onSubmit={handleFormSubmit}
      initialValues={initialValues}
      keepDirtyOnReinitialize
      render={formRenderProps => {
        const { handleSubmit, form, values, inProgress, invalid, submitError } = formRenderProps;

        let bookingDateText = <FormattedMessage id="CheckoutPage.datesFieldPlaceholderPackage" />;
        const dateValues = values && values.dates;

        const isSingleDay =
          daysAdded(dateValues && dateValues.startDate, dateValues && dateValues.endDate) === 1;

        if (dateValues) {
          bookingDateText = `${format(dateValues.startDate, 'MMM DD, YYYY')} - ${format(
            dateValues.endDate,
            'MMM DD, YYYY'
          )}`;
        }
        if (dateValues && isSingleDay) {
          bookingDateText = format(dateValues.startDate, 'MMM DD, YYYY');
        }

        const { startDate, endDate, startLodgingDate } = dateValues ?? {};
        const formattedStartDate = moment(startDate).format('YYYY-MM-DD');
        const formattedEndDate = moment(endDate).format('YYYY-MM-DD');

        let packageItem = null;

        const packagePrice = JSON.parse(packageInfo.price);
        const packageFee = new Money(packagePrice.amount, config.currency);
        const includeLodgings = values?.lodgingAddons?.length > 0 || hasLodging;

        const today = moment();

        const minDaysEndDate = moment().add(days?.min - 1 || 0, 'd');
        // Handle both standard party size and flexible pricing data
        let partySizeMaybe = packageMinParty;
        let flexiblePricingBookingData = null;

        if (values.partySize) {
          if (typeof values.partySize === 'object' && values.partySize.partySize) {
            // Flexible pricing data
            partySizeMaybe = parseInt(values.partySize.partySize, 10);
            flexiblePricingBookingData = values.partySize.flexibleGuestSize;
          } else {
            // Standard party size
            partySizeMaybe = parseInt(values.partySize, 10);
          }
        }
        let lodgingFees = (values.lodgingAddons || []).map(addon => {
          const lodgingPrice = JSON.parse(optionalLodging[addon].price, sdkTypes.reviver);

          return {
            price: lodgingPrice,
            type: optionalLodging[addon].type,
          };
        });

        if (includedLodging?.length) {
          lodgingFees = lodgingFees.concat(
            includedLodging.map(item => {
              const lodgingPrice = JSON.parse(item.price, sdkTypes.reviver);

              return {
                price: lodgingPrice,
                type: item.type,
              };
            })
          );
        }

        packageItem = {
          code: `line-item/${packageInfo.title.replace(/\s/g, '-')}`,
          title: packageInfo.title,
          includeFor: ['customer', 'provider'],
          unitPrice: packageFee,
          quantity: new Decimal(1),
          lineTotal: packageFee,
          reversal: false,
          guestSize: partySizeMaybe,
          activity: packageInfo.activity,
          hasLodging: includeLodgings,
          lodgingFees,
        };

        const rvDetails = getRVDetails(listing, packageInfo);

        const dynamicBadgesItem = {
          days: {
            min:
              startDate && endDate
                ? daysAdded(startDate, endDate)
                : daysAdded(today, minDaysEndDate),
          },
          guests: { min: partySizeMaybe },
          hasLodging: includeLodgings && (minDaysEndDate > 1 || daysAdded(startDate, endDate) > 1),
          isRvFriendly: rvDetails.isRvFriendly,
          hasRVSites: rvDetails.hasRVSites,
        };

        const lodgingTax = dynamicBadgesItem.hasLodging ? getLodgingTax(listing) : null;

        const bookingData = {
          unitType,
          unitPrice,
          startDate: startDate || today,
          endDate: endDate || minDaysEndDate,
          startLodgingDate,
          quantity: 1,
          partySize: partySizeMaybe,
          packageItem,
          lodgingTax,
          isInitial: !dateValues,
          flexiblePricing: flexiblePricingBookingData,
        };

        const submitDisabled =
          invalid ||
          inProgress ||
          !startDate ||
          !endDate ||
          listingState !== 'published' ||
          !isPublished;

        const classes = classNames(
          rootClassName || css.root,
          sticky && submitDisabled && css.rootSticky
        );

        let calendarInfoDays;

        if (days.min === days.max) {
          calendarInfoDays = days.min;
        } else if (days.max === 365) {
          calendarInfoDays = `${days.min}+`;
        } else {
          calendarInfoDays = `${days.min}-${days.max}`;
        }

        const showLodgingAddOn =
          optionalLodging?.length > 0 && (packageInfo.days.min > 1 || !isSingleDay);

        return (
          <div className={classes}>
            <div className={css.formWrapper}>
              <div className={css.priceWrapper}>
                <div className={css.priceContainer}>
                  <PackageStartingPrice
                    intl={intl}
                    startingPrice={startingPrice}
                    priceClassName={css.priceText}
                    unitClassName={css.perGuestText}
                  />
                </div>
              </div>

              <div className={css.packageIncludes}>
                <div className={css.packageIncludesHeading}>
                  <FormattedMessage id="PackagePage.priceIncludes" />
                </div>
                <PackageBadges packageInfo={packageInfo} listing={listing} />
              </div>

              <div className={css.formWrapper}>
                {isBlockedByEarlyAccess && isAvailable && !outOfBookingRange ? (
                  <EarlyAccessButtonWithTimer
                    expirationTimestamp={packageInfo?.earlyAccessExpiration}
                    setIsModalOpen={setIsEarlyAccessModalOpen}
                  />
                ) : (
                  <PackagePricingForm
                    handleSubmit={handleSubmit}
                    calendarInfoDays={calendarInfoDays}
                    bookingDateText={bookingDateText}
                    form={form}
                    startDate={startDate}
                    endDate={endDate}
                    formattedStartDate={formattedStartDate}
                    formattedEndDate={formattedEndDate}
                    showLodgingAddOn={showLodgingAddOn}
                    lodgingAddons={values.lodgingAddons}
                    inProgress={inProgress}
                    submitDisabled={submitDisabled}
                    submitError={submitError}
                    bookingData={bookingData}
                    onTransactionPriceChange={onPriceChange}
                    showCredova={showCredova}
                    setIsCredovaModalOpen={setIsCredovaModalOpen}
                    hasLodging={hasLodging}
                    dynamicHasLodging={dynamicBadgesItem.hasLodging}
                    dateValues={dateValues}
                    availableDays={availableDays}
                    packageInfo={packageInfo}
                    packageMinParty={packageMinParty}
                    packageMaxParty={packageMaxParty}
                    timeSlots={timeSlots}
                    listing={listing}
                    optionalLodging={optionalLodging}
                    intl={intl}
                    isAvailable={isAvailable}
                    outOfBookingRange={outOfBookingRange}
                    formattedPackageAvailableDate={formattedPackageAvailableDate}
                    availabilityPackageBadges={availabilityPackageBadges}
                    setIsPackageAvailabilityModalOpen={setIsPackageAvailabilityModalOpen}
                    isOnWaitingList={isOnWaitingList}
                    onMessage={onMessage}
                    onBookingSelectionChange={onBookingSelectionChange}
                    partySizeValue={values.partySize}
                    landownerFirstName={landownerFirstName}
                    isOwnListing={isOwnListing}
                  />
                )}
              </div>

              <div className={css.formFieldWrapper}>
                <Banner
                  header={intl.formatMessage({ id: 'PricingDetails.bannerHeader' })}
                  body={intl.formatMessage({ id: 'PricingDetails.bannerText' })}
                />
              </div>
            </div>
          </div>
        );
      }}
    />
  );
};

PackagePricingPanel.defaultProps = {
  unitType: config.bookingUnitType,
  timeSlots: null,
  onPriceChange: null,
  showCredova: false,
  onMessage: null,
  landownerFirstName: null,
  isOwnListing: false,
};

PackagePricingPanel.propTypes = {
  unitType: propTypes.bookingUnitType,
  history: shape({
    push: func.isRequired,
  }).isRequired,
  listing: propTypes.listing.isRequired,
  packageInfo: propTypes.package.isRequired,
  timeSlots: arrayOf(propTypes.timeSlot),
  startingPrice: propTypes.money.isRequired,
  currentUser: propTypes.currentUser.isRequired,
  callSetInitialValues: func.isRequired,
  onInitializeCardPaymentData: func.isRequired,
  currentUserInit: bool.isRequired,
  onPriceChange: func,
  showCredova: bool,
  onMessage: func,
  landownerFirstName: string,
  isOwnListing: bool,
};

const mapDispatchToProps = dispatch => ({
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
});

export default compose(
  withRouter,
  connect(
    null,
    mapDispatchToProps
  ),
  injectIntl
)(PackagePricingPanel);
